import React from 'react';
import { Box, Grid, MenuItem } from '@material-ui/core';
import { FormStatus } from '@lib/state';
import {
  EnumValueDisplay,
  ErrorDisplay,
  Feedback,
  FormNumberField,
  FormSelect,
  FormSubmitButton,
  MAX_PREFERRED_TEMPERATURE,
  MIN_PREFERRED_TEMPERATURE,
  Section,
} from '@lib/common';
import { ThermostatMode, ThermostatRoomBehavior } from 'app/state';

export interface PropertyIoTSettingsFormFields {
  minTemperature?: number;
  maxTemperature?: number;
  defaultTemperature?: number;
  defaultThermostatMode?: ThermostatMode;
  temperatureOffset?: number;
  awayMinTemperatureF?: number;
  awayMaxTemperatureF?: number;
  awayOffsetF?: number;
  vacantMinTemperatureF?: number;
  awayBehavior: ThermostatRoomBehavior;
  vacantBehavior: ThermostatRoomBehavior;
  vacantMaxTemperatureF?: number;
}

interface Props {
  status: FormStatus;
  error?: Error;
  resetUI?: () => void;
}

export const PropertyIoTSettingsForm: React.FC<Props> = ({ status, error, resetUI }) => {
  return (
    <>
      <Section title="Thermostat Settings">
        {/* same range guests can pick on My Account and My Key */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormNumberField
              name="minTemperature"
              label="Minimum Temperature"
              fullWidth
              validationOptions={{
                min: {
                  value: MIN_PREFERRED_TEMPERATURE,
                  message: `Minimum temperature must be at least ${MIN_PREFERRED_TEMPERATURE}`,
                },
                max: {
                  value: MAX_PREFERRED_TEMPERATURE,
                  message: `Maximum temperature must be at most ${MAX_PREFERRED_TEMPERATURE}`,
                },
                required: 'Minimum temperature is required',
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormNumberField
              name="maxTemperature"
              label="Maximum Temperature"
              fullWidth
              validationOptions={{
                min: {
                  value: MIN_PREFERRED_TEMPERATURE,
                  message: `Minimum temperature must be at least ${MIN_PREFERRED_TEMPERATURE}`,
                },
                max: {
                  value: MAX_PREFERRED_TEMPERATURE,
                  message: `Maximum temperature must be at most ${MAX_PREFERRED_TEMPERATURE}`,
                },
                required: 'Maximum temperature is required',
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormNumberField
              name="defaultTemperature"
              label="Default Temperature"
              fullWidth
              validationOptions={{
                min: {
                  value: MIN_PREFERRED_TEMPERATURE,
                  message: `Minimum temperature must be at least ${MIN_PREFERRED_TEMPERATURE}`,
                },
                max: {
                  value: MAX_PREFERRED_TEMPERATURE,
                  message: `Maximum temperature must be at most ${MAX_PREFERRED_TEMPERATURE}`,
                },
                required: 'Default temperature is required',
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormSelect name="defaultThermostatMode" label="Default Mode" fullWidth>
              {Object.values(ThermostatMode).map(x => (
                <MenuItem key={x} value={x}>
                  <EnumValueDisplay value={x} />
                </MenuItem>
              ))}
            </FormSelect>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormNumberField
              name="temperatureOffset"
              label="Temperature Offset"
              fullWidth
              integer
              radix={10}
              validationOptions={{
                min: { value: -10, message: 'Temperature offset must be at least -10' },
                max: { value: 10, message: 'Temperature offset must be at most 10' },
                required: 'Temperature offset is required',
              }}
            />
          </Grid>
        </Grid>
      </Section>
      <Section title="Away Temperature Settings">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormNumberField
              name="awayMinTemperatureF"
              label="Minimum Temperature"
              fullWidth
              validationOptions={{
                min: { value: 60, message: 'Minimum temperature must be at least 60' },
                max: { value: 90, message: 'Maximum temperature must be at most 90' },
                required: 'Minimum temperature is required',
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormNumberField
              name="awayMaxTemperatureF"
              label="Maximum Temperature"
              fullWidth
              validationOptions={{
                min: { value: 60, message: 'Maximum temperature must be at least 60' },
                max: { value: 90, message: 'Maximum temperature must be at most 90' },
                required: 'Maximum temperature is required',
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormNumberField
              name="awayOffsetF"
              label="Temperature Offset"
              fullWidth
              integer
              radix={10}
              validationOptions={{
                min: { value: -10, message: 'Temperature offset must be at least -10' },
                max: { value: 10, message: 'Temperature offset must be at most 10' },
                required: 'Temperature offset is required',
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormSelect
              name="awayBehavior"
              label="Behavior"
              fullWidth
              validationOptions={{ required: 'Behavior is required' }}
            >
              {Object.values(ThermostatRoomBehavior)
                .filter(x => x !== 'OFF')
                .map(x => (
                  <MenuItem key={x} value={x}>
                    <EnumValueDisplay value={x} />
                  </MenuItem>
                ))}
            </FormSelect>
          </Grid>
        </Grid>
      </Section>
      <Section title="Vacant Temperature Settings">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormNumberField
              name="vacantMinTemperatureF"
              label="Minimum Temperature"
              fullWidth
              validationOptions={{
                min: { value: 60, message: 'Minimum temperature must be at least 60' },
                max: { value: 90, message: 'Maximum temperature must be at most 90' },
                required: 'Minimum temperature is required',
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormNumberField
              name="vacantMaxTemperatureF"
              label="Maximum Temperature"
              fullWidth
              validationOptions={{
                min: { value: 60, message: 'Maximum temperature must be at least 60' },
                max: { value: 90, message: 'Maximum temperature must be at most 90' },
                required: 'Maximum temperature is required',
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormSelect
              name="vacantBehavior"
              label="Behavior"
              fullWidth
              validationOptions={{ required: 'Behavior is required' }}
            >
              {Object.values(ThermostatRoomBehavior)
                .filter(x => x !== 'RELATIVE')
                .map(x => (
                  <MenuItem key={x} value={x}>
                    <EnumValueDisplay value={x} />
                  </MenuItem>
                ))}
            </FormSelect>
          </Grid>
        </Grid>
      </Section>
      <Feedback show={status === FormStatus.Success} severity="success" onHide={resetUI}>
        Thermostat settings saved successfully!
      </Feedback>
      <ErrorDisplay error={error} />
      <Box display="flex" justifyContent="flex-end" my={1}>
        <FormSubmitButton
          type="submit"
          color="secondary"
          variant="contained"
          disabled={status === FormStatus.Pending}
        >
          Save Thermostat Settings
        </FormSubmitButton>
      </Box>
    </>
  );
};
